/** «Остаток ФБС» — источник и разбивка, ЧИСТЫЕ правила без React.
 *
 *  У склада ФБС явный источник остатка (`stock_source`, владелец 15.10.2026):
 *  - `wh_helper` — пул комнаты, ТОТ ЖЕ, что синк wh-helper публикует в WB: свободный 1С всех
 *    юрлиц комнаты − собрано порталом склада, но не проведено в 1С − несобранные задания WB/Ozon.
 *    Без клампа: минус значит «обещано больше, чем лежит», и в потребность он идёт как есть;
 *  - `onec` — свободный 1С организации склада (региональные склады, как было).
 *
 *  Разбивка обязана сходиться с числом в колонке: не сошлась — это говорится словами в «почему»,
 *  а не прячется (контракт бэка: `stock_qty = Σ by_org − in_flight − wb − ozon`).
 */
import type { FbsReport, FbsStockDetail, FbsStockSource } from "@/api/supply";
import { fmtDateTime, pad2 } from "./format";

/** Коротко — для шапки отчёта и списка складов. */
export const STOCK_SOURCE_SHORT: Record<FbsStockSource, string> = {
  wh_helper: "wh-helper",
  onec: "1С",
};

/** Полностью — для выбора в форме склада. */
export const STOCK_SOURCE_LABEL: Record<FbsStockSource, string> = {
  wh_helper: "wh-helper — пул комнаты, как в WB",
  onec: "1С — свободный остаток организации склада",
};

export const STOCK_SOURCES: readonly FbsStockSource[] = ["wh_helper", "onec"];

/** Подсказка у отрицательного «Остатка ФБС». */
export const NEGATIVE_STOCK_HINT = "Несобранных заказов больше, чем лежит в комнате";

/** Часы и минуты ЛОКАЛЬНОГО времени («14:05»). Неразобранная строка показывается как есть —
 *  не подменяется ни «—», ни текущим временем. */
export function fmtClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function sameLocalDay(a: string, b: string): boolean {
  const x = new Date(a);
  const y = new Date(b);
  return (
    x.getFullYear() === y.getFullYear() &&
    x.getMonth() === y.getMonth() &&
    x.getDate() === y.getDate()
  );
}

/** Шапка отчёта: откуда «Остаток ФБС» и подсказка к нему (`null` — подсказывать нечего). */
export function stockSourceHeader(
  report: Pick<FbsReport, "stock_source" | "stock_as_of" | "stock_last_sync_at">,
): { text: string; title: string | null } {
  if (report.stock_source === "onec") return { text: "остаток ФБС: 1С", title: null };
  const sync = report.stock_last_sync_at;
  // синк прошёл не сегодня — одних часов мало, нужна дата
  const syncText =
    sync === null
      ? "не было"
      : sameLocalDay(sync, report.stock_as_of)
        ? fmtClock(sync)
        : fmtDateTime(sync);
  return {
    text: `остаток ФБС: wh-helper на ${fmtClock(report.stock_as_of)}`,
    title:
      "Тот же пул комнаты, что синк публикует в WB: свободный 1С всех юрлиц комнаты − собрано, " +
      `но не проведено в 1С − несобранные заказы WB/Ozon; синк: ${syncText}`,
  };
}

/** Слагаемое разбивки: `+` — свободный 1С юрлица, `−` — то, что из него уже обещано. */
export interface StockTerm {
  key: string;
  /** Что за слагаемое — по нему карточка остатка подписывает строку своими словами. */
  kind: "org" | "in_flight" | "wb" | "ozon";
  /** Юрлицо (`kind: "org"`): ссылка и имя как пришли (имени может не быть). */
  ref?: string;
  name?: string | null;
  label: string;
  sign: "+" | "−";
  qty: number;
  /** Полная ссылка юрлица — когда в подписи её нет (имя известно или подпись общая). */
  title?: string;
}

export interface StockBreakdown {
  terms: StockTerm[];
  /** Сумма слагаемых — должна совпасть с «Остатком ФБС» строки. */
  sum: number;
  /** Разбивка не сходится с числом в колонке (контракт бэка нарушен) — показать, не прятать. */
  mismatch: boolean;
}

/** Разбивка «Остатка ФБС» строки. Юрлица показываются все (видно, из кого состоит комната),
 *  нулевые вычитаемые скрыты. Имени юрлица нет — у wh-helper показываем ref (как есть), у `onec`
 *  юрлицо одно и это организация склада. */
export function stockBreakdown(detail: FbsStockDetail, stockQty: number): StockBreakdown {
  const terms: StockTerm[] = detail.by_org.map((o) => ({
    key: `org:${o.ref}`,
    kind: "org",
    ref: o.ref,
    name: o.name,
    label:
      o.name !== null
        ? `1С: ${o.name}`
        : detail.source === "onec"
          ? "1С: организация склада"
          : `1С: ${o.ref}`,
    sign: "+",
    qty: o.qty,
    title: o.name !== null || detail.source === "onec" ? o.ref : undefined,
  }));
  const minus: [StockTerm["kind"], string, number][] = [
    ["in_flight", "собрано, не проведено в 1С", detail.in_flight],
    ["wb", "несобранные WB", detail.wb_unassembled],
    ["ozon", "несобранные Ozon", detail.ozon_unassembled],
  ];
  for (const [kind, label, qty] of minus)
    if (qty !== 0) terms.push({ key: kind, kind, label, sign: "−", qty });
  const sum = terms.reduce((s, t) => s + (t.sign === "+" ? t.qty : -t.qty), 0);
  return { terms, sum, mismatch: Math.abs(sum - stockQty) > 1e-6 };
}
